/**
 * Mock Analytics Data Generator
 *
 * Generates realistic historical patient analytics records for dashboard demonstration
 * Isolated utility - does not modify existing patient data
 */

import { WorkflowStage, PredictProgram, DisqualificationReason, ChangeType } from '~/types/predict';
import { PatientAnalyticsRecord, StageTransition, DerivedMetrics, AnalyticsFilters } from '~/types/analytics';

const FACILITIES = [
  { id: 'client-metro', name: 'Metro General Hospital' },
  { id: 'client-city', name: 'City Medical Center' },
];

const OWNERS = [
  { id: 'user-admin', name: 'Sarah Johnson' },
  { id: 'user-metro-coordinator', name: 'Michael Chen' },
  { id: 'user-city-coordinator', name: 'Jennifer Martinez' },
  { id: 'user-regional-manager', name: 'David Thompson' },
];

const PROGRAMS = [
  PredictProgram.HOME_HEALTH,
  PredictProgram.SNF_AT_HOME,
  PredictProgram.HOSPICE,
  PredictProgram.INFUSION_AT_HOME,
  PredictProgram.PALLIATIVE,
];

const BLOCKERS = [
  'Insurance authorization pending',
  'Caregiver assessment incomplete',
  'Home safety evaluation needed',
  'DME order not placed',
  'Physician orders missing',
  'Patient education not documented',
  'Agency coordination pending',
];

const DISQUALIFICATION_REASONS = [
  DisqualificationReason.CLINICALLY_UNSTABLE,
  DisqualificationReason.NO_CAREGIVER,
  DisqualificationReason.OUT_OF_SERVICE_AREA,
  DisqualificationReason.PATIENT_DECLINED,
  DisqualificationReason.INSURANCE_DENIAL,
  DisqualificationReason.ACUITY_TOO_HIGH,
];

const PATIENT_FIRST_NAMES = [
  'James', 'Mary', 'John', 'Patricia', 'Robert', 'Jennifer', 'Michael', 'Linda',
  'William', 'Barbara', 'David', 'Elizabeth', 'Richard', 'Susan', 'Joseph', 'Jessica',
  'Thomas', 'Sarah', 'Charles', 'Karen', 'Christopher', 'Nancy', 'Daniel', 'Lisa',
  'Matthew', 'Betty', 'Anthony', 'Margaret', 'Mark', 'Sandra', 'Donald', 'Ashley',
  'Steven', 'Kimberly', 'Paul', 'Emily', 'Andrew', 'Donna', 'Joshua', 'Michelle',
  'Kenneth', 'Dorothy', 'Kevin', 'Carol', 'Brian', 'Amanda', 'George', 'Melissa',
  'Edward', 'Deborah', 'Ronald', 'Stephanie', 'Timothy', 'Rebecca', 'Jason', 'Sharon',
  'Jeffrey', 'Laura', 'Ryan', 'Cynthia', 'Jacob', 'Kathleen', 'Gary', 'Amy',
  'Nicholas', 'Shirley', 'Eric', 'Angela', 'Jonathan', 'Helen', 'Stephen', 'Anna',
  'Larry', 'Brenda', 'Justin', 'Pamela', 'Scott', 'Nicole', 'Brandon', 'Emma',
];

const PATIENT_LAST_NAMES = [
  'Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller', 'Davis',
  'Rodriguez', 'Martinez', 'Hernandez', 'Lopez', 'Gonzalez', 'Wilson', 'Anderson', 'Thomas',
  'Taylor', 'Moore', 'Jackson', 'Martin', 'Lee', 'Perez', 'Thompson', 'White',
  'Harris', 'Sanchez', 'Clark', 'Ramirez', 'Lewis', 'Robinson', 'Walker', 'Young',
  'Allen', 'King', 'Wright', 'Scott', 'Torres', 'Nguyen', 'Hill', 'Flores',
  'Green', 'Adams', 'Nelson', 'Baker', 'Hall', 'Rivera', 'Campbell', 'Mitchell',
  'Carter', 'Roberts', 'Gomez', 'Phillips', 'Evans', 'Turner', 'Diaz', 'Parker',
  'Cruz', 'Edwards', 'Collins', 'Reyes', 'Stewart', 'Morris', 'Morales', 'Murphy',
  'Cook', 'Rogers', 'Gutierrez', 'Ortiz', 'Morgan', 'Cooper', 'Peterson', 'Bailey',
  'Reed', 'Kelly', 'Howard', 'Ramos', 'Kim', 'Cox', 'Ward', 'Richardson',
];

/**
 * Seeded random number generator for deterministic demo data
 * Using a simple LCG (Linear Congruential Generator)
 */
let seed = 12345; // Fixed seed for consistent demo data

const seededRandom = (): number => {
  seed = (seed * 1103515245 + 12345) & 0x7fffffff;
  return seed / 0x7fffffff;
};

/**
 * Generate random date within range (seeded for consistency)
 */
const randomDate = (start: Date, end: Date): Date => {
  return new Date(start.getTime() + seededRandom() * (end.getTime() - start.getTime()));
};

/**
 * Generate random int between min and max (inclusive, seeded for consistency)
 */
const randomInt = (min: number, max: number): number => {
  return Math.floor(seededRandom() * (max - min + 1)) + min;
};

/**
 * Pick random item from array (seeded for consistency)
 */
const randomPick = <T,>(array: T[]): T => {
  return array[Math.floor(seededRandom() * array.length)];
};

/**
 * Generate stage transitions for a patient
 *
 * Realistic discharge workflow timelines for low-70s SLA compliance (~70-73%):
 * - Triage: 24 hours minimum (initial assessment)
 * - Eligibility: 16-28 hours (checklist completion, insurance verification)
 * - Acceptance: 10-18 hours (program acceptance, coordination)
 * - Transition: 8-16 hours (final arrangements, discharge execution)
 * - Total typical flow: 58-86 hours (2.4-3.6 days) - 70% within SLA, 30% exceed
 */
const generateStageTransitions = (
  admissionDate: Date,
  completed: boolean,
  disqualified: boolean,
  hoursInSystem: number
): StageTransition[] => {
  const transitions: StageTransition[] = [];
  const now = new Date();

  // STAGE 1: TRIAGE - STRICT 24 HOUR MINIMUM
  // ALL patients < 24h old MUST be in Triage, no exceptions
  const triageEntryDate = new Date(admissionDate);
  const triageDurationHours = 24; // Fixed minimum
  const triageExitDate = new Date(admissionDate.getTime() + triageDurationHours * 60 * 60 * 1000);

  // If patient hasn't been in system for 24 hours yet
  if (hoursInSystem < 24) {
    transitions.push({
      stage: WorkflowStage.TRIAGE,
      enteredAt: triageEntryDate.toISOString(),
      // No exit date - still in progress
    });
    return transitions;
  }

  // Completed Triage
  transitions.push({
    stage: WorkflowStage.TRIAGE,
    enteredAt: triageEntryDate.toISOString(),
    exitedAt: triageExitDate.toISOString(),
  });

  // Check for disqualification
  if (disqualified) {
    transitions.push({
      stage: WorkflowStage.DISQUALIFIED,
      enteredAt: triageExitDate.toISOString(),
    });
    return transitions;
  }

  // STAGE 2: ELIGIBILITY (16-28 hours)
  const eligibilityDurationHours = randomInt(16, 28);
  const eligibilityEntryDate = triageExitDate;
  const eligibilityExitDate = new Date(eligibilityEntryDate.getTime() + eligibilityDurationHours * 60 * 60 * 1000);
  const totalHoursToCompleteEligibility = 24 + eligibilityDurationHours; // Triage is always 24h

  // If patient hasn't been in system long enough to complete eligibility
  if (hoursInSystem < totalHoursToCompleteEligibility) {
    transitions.push({
      stage: WorkflowStage.ELIGIBILITY,
      enteredAt: eligibilityEntryDate.toISOString(),
      // No exit date - still in progress
    });
    return transitions;
  }

  // Completed Eligibility (or stuck if not completed overall)
  transitions.push({
    stage: WorkflowStage.ELIGIBILITY,
    enteredAt: eligibilityEntryDate.toISOString(),
    exitedAt: completed ? eligibilityExitDate.toISOString() : undefined,
  });

  if (!completed) {
    // Patient stuck in eligibility
    return transitions;
  }

  // STAGE 3: ACCEPTANCE (10-18 hours)
  const acceptanceDurationHours = randomInt(10, 18);
  const acceptanceEntryDate = eligibilityExitDate;
  const acceptanceExitDate = new Date(acceptanceEntryDate.getTime() + acceptanceDurationHours * 60 * 60 * 1000);
  const totalHoursToCompleteAcceptance = totalHoursToCompleteEligibility + acceptanceDurationHours;

  // If patient hasn't been in system long enough to complete acceptance
  if (hoursInSystem < totalHoursToCompleteAcceptance) {
    transitions.push({
      stage: WorkflowStage.ACCEPTANCE,
      enteredAt: acceptanceEntryDate.toISOString(),
    });
    return transitions;
  }

  transitions.push({
    stage: WorkflowStage.ACCEPTANCE,
    enteredAt: acceptanceEntryDate.toISOString(),
    exitedAt: acceptanceExitDate.toISOString(),
  });

  // STAGE 4: TRANSITION (8-16 hours)
  const transitionDurationHours = randomInt(8, 16);
  const transitionEntryDate = acceptanceExitDate;
  const transitionExitDate = new Date(transitionEntryDate.getTime() + transitionDurationHours * 60 * 60 * 1000);

  transitions.push({
    stage: WorkflowStage.TRANSITION,
    enteredAt: transitionEntryDate.toISOString(),
    exitedAt: transitionExitDate.toISOString(),
  });

  return transitions;
};

/**
 * Get the current "day" boundary at 3am MT for consistent daily refresh
 * This ensures "new candidates" refresh at 3am MT each day
 *
 * Example:
 * - Before 3am MT on Tuesday: returns Monday 3am MT
 * - After 3am MT on Tuesday: returns Tuesday 3am MT
 */
export const getCurrentDayAt3amMT = (): Date => {
  const now = new Date();

  // Convert to MT (UTC-7 in summer, UTC-6 in winter, using UTC-7 for simplicity)
  const mtOffset = -7 * 60; // minutes
  const nowMT = new Date(now.getTime() + (mtOffset * 60 * 1000));

  // If before 3am MT today, use yesterday's 3am MT
  // If after 3am MT today, use today's 3am MT
  const todayAt3amMT = new Date(nowMT);
  todayAt3amMT.setHours(3, 0, 0, 0);

  if (nowMT < todayAt3amMT) {
    // It's before 3am MT, so use yesterday's 3am
    todayAt3amMT.setDate(todayAt3amMT.getDate() - 1);
  }

  // Convert back to local time
  return new Date(todayAt3amMT.getTime() - (mtOffset * 60 * 1000));
};

/**
 * Generate a single patient analytics record
 */
const generatePatientRecord = (id: number, dateRange: { start: Date; end: Date }): PatientAnalyticsRecord => {
  const facility = randomPick(FACILITIES);
  const program = randomPick(PROGRAMS);

  // DEMO FEATURE: First 5 patients (IDs 0-4) are always "new candidates"
  // Their admission timestamps update daily at 3am MT to always appear as "admitted today"
  // This ensures there are always 5 new candidates visible for demo purposes
  // Production: Would use actual real-time admission data
  let admissionDate: Date;
  if (id < 5) {
    const now = new Date();
    const dayBoundary = getCurrentDayAt3amMT();

    // Calculate how many hours have passed since the day boundary
    const hoursSinceBoundary = (now.getTime() - dayBoundary.getTime()) / (1000 * 60 * 60);

    // Admit them somewhere between 2 hours and current hours after the boundary
    // This ensures they're always "today" but never in the future
    const maxHoursAfter = Math.min(hoursSinceBoundary, 23);
    const minHoursAfter = Math.min(2, maxHoursAfter);
    const hoursAfter3am = randomInt(minHoursAfter, Math.max(minHoursAfter, maxHoursAfter));

    admissionDate = new Date(dayBoundary.getTime() + (hoursAfter3am * 60 * 60 * 1000));
  } else {
    admissionDate = randomDate(dateRange.start, dateRange.end);
  }

  // Calculate how long patient has been in system
  const now = new Date();
  const hoursInSystem = (now.getTime() - admissionDate.getTime()) / (1000 * 60 * 60);
  const daysInSystem = hoursInSystem / 24;

  // Determine patient status based on time in system
  // Balanced workflow timelines: 58-86 hours (2.4-3.6 days) typical - targeting low 70s SLA compliance (~70-73%)
  // Adjusted to ensure good distribution across Acceptance and Transition stages
  let completed = false;
  let disqualified = false;

  if (hoursInSystem < 24) {
    // Less than 24h: MUST be in Triage, cannot be completed
    completed = false;
    disqualified = Math.random() < 0.06; // 6% early disqualification
  } else if (hoursInSystem < 40) {
    // 24-40h: In Eligibility, some disqualifications
    completed = false;
    disqualified = Math.random() < 0.12; // 12% disqualification
  } else if (hoursInSystem < 52) {
    // 40-52h: Very few complete - still in Eligibility or early Acceptance
    completed = Math.random() < 0.10;
    disqualified = !completed && Math.random() < 0.14; // 14% of non-completed
  } else if (hoursInSystem < 64) {
    // 52-64h (2.2-2.7 days): Some in Acceptance, some starting Transition
    completed = Math.random() < 0.35;
    disqualified = !completed && Math.random() < 0.16; // 16% of non-completed
  } else if (hoursInSystem < 72) {
    // 64-72h (2.7-3 days): Many in Transition, some complete - at SLA threshold
    completed = Math.random() < 0.60;
    disqualified = !completed && Math.random() < 0.18; // 18% of non-completed
  } else if (hoursInSystem < 84) {
    // 72-84h (3-3.5 days): Most complete here - missed SLA
    completed = Math.random() < 0.85;
    disqualified = !completed && Math.random() < 0.22; // 22% of non-completed
  } else if (hoursInSystem < 96) {
    // 84-96h (3.5-4 days): Nearly all complete
    completed = Math.random() < 0.93;
    disqualified = !completed && Math.random() < 0.28; // 28% of non-completed
  } else if (daysInSystem < 7) {
    // 4-7 days: Nearly all completed
    completed = Math.random() < 0.97;
    disqualified = !completed && Math.random() < 0.35; // 35% of non-completed
  } else {
    // 7+ days: All should be completed, rest are problem cases
    completed = Math.random() < 0.99;
    disqualified = !completed && Math.random() < 0.45; // 45% of non-completed
  }

  // Generate stage transitions first so we know current stage
  const transitions = generateStageTransitions(admissionDate, completed, disqualified, hoursInSystem);
  const currentStage = transitions[transitions.length - 1].stage;

  // Assignment based on current stage (new/triage patients less likely to be assigned)
  let assignmentChance: number;
  if (currentStage === WorkflowStage.TRIAGE && hoursInSystem < 4) {
    assignmentChance = 0.3; // Very new patients rarely assigned yet
  } else if (currentStage === WorkflowStage.TRIAGE) {
    assignmentChance = 0.6; // Older triage patients more likely assigned
  } else if (currentStage === WorkflowStage.DISQUALIFIED) {
    assignmentChance = 0.5; // Disqualified might have been assigned before DQ
  } else {
    assignmentChance = 0.9; // Almost all patients in eligibility+ are assigned
  }
  const owner = Math.random() < assignmentChance ? randomPick(OWNERS) : null;

  // First review happens 1-12 hours after admission
  const firstReviewDelay = randomInt(1, 12) * 60 * 60 * 1000;
  const firstReviewTimestamp = new Date(admissionDate.getTime() + firstReviewDelay).toISOString();

  // 12% override rate - but only for patients in Eligibility or beyond
  const canHaveOverride = currentStage === WorkflowStage.ELIGIBILITY ||
                          currentStage === WorkflowStage.ACCEPTANCE ||
                          currentStage === WorkflowStage.TRANSITION ||
                          completed;
  const isOverride = canHaveOverride && Math.random() < 0.12;

  // Selected program logic based on stage
  let selectedProgram: PredictProgram | null = null;
  if (currentStage === WorkflowStage.TRIAGE) {
    // No selected program yet in Triage - still assessing
    selectedProgram = null;
  } else if (currentStage === WorkflowStage.DISQUALIFIED) {
    // May or may not have had a selected program before disqualification
    selectedProgram = Math.random() < 0.5
      ? (isOverride ? randomPick(PROGRAMS.filter(p => p !== program)) : program)
      : null;
  } else {
    // MUST have selected program in Eligibility, Acceptance, Transition, or Completed
    selectedProgram = isOverride ? randomPick(PROGRAMS.filter(p => p !== program)) : program;
  }

  // Readiness based on stage
  let readinessPercent: number;
  if (currentStage === WorkflowStage.TRIAGE) {
    readinessPercent = randomInt(0, 40);
  } else if (currentStage === WorkflowStage.ELIGIBILITY) {
    readinessPercent = randomInt(40, 75);
  } else if (currentStage === WorkflowStage.ACCEPTANCE) {
    readinessPercent = randomInt(75, 95);
  } else if (currentStage === WorkflowStage.TRANSITION) {
    readinessPercent = randomInt(95, 100);
  } else if (completed) {
    readinessPercent = 100;
  } else {
    readinessPercent = randomInt(20, 60);
  }

  // Checklist completion based on stage
  let checklistCompletionPercent: number;
  if (currentStage === WorkflowStage.TRIAGE) {
    checklistCompletionPercent = randomInt(0, 30);
  } else if (currentStage === WorkflowStage.ELIGIBILITY) {
    checklistCompletionPercent = randomInt(50, 85);
  } else if (currentStage === WorkflowStage.ACCEPTANCE || currentStage === WorkflowStage.TRANSITION || completed) {
    checklistCompletionPercent = randomInt(90, 100);
  } else {
    checklistCompletionPercent = randomInt(0, 50);
  }

  // Blockers - more common in early stages, decrease as workflow progresses
  let blockerCount = 0;
  if (!completed && currentStage !== WorkflowStage.DISQUALIFIED) {
    if (currentStage === WorkflowStage.TRIAGE) {
      blockerCount = Math.random() < 0.6 ? randomInt(1, 3) : 0;
    } else if (currentStage === WorkflowStage.ELIGIBILITY) {
      blockerCount = Math.random() < 0.4 ? randomInt(1, 2) : 0;
    } else if (currentStage === WorkflowStage.ACCEPTANCE) {
      blockerCount = Math.random() < 0.2 ? 1 : 0;
    } else if (currentStage === WorkflowStage.TRANSITION) {
      blockerCount = Math.random() < 0.1 ? 1 : 0;
    }
  }

  const blockers: string[] = [];
  for (let i = 0; i < blockerCount; i++) {
    const blocker = randomPick(BLOCKERS);
    if (!blockers.includes(blocker)) {
      blockers.push(blocker);
    }
  }

  // Program score
  const programScore = disqualified ? randomInt(30, 60) : randomInt(65, 95);

  // Generate patient name
  const firstName = randomPick(PATIENT_FIRST_NAMES);
  const lastName = randomPick(PATIENT_LAST_NAMES);
  const patientName = `${firstName} ${lastName}`;

  const record: PatientAnalyticsRecord = {
    patientId: `analytics-patient-${id}`,
    patientName,
    facilityId: facility.id,
    facilityName: facility.name,
    ownerId: owner?.id || null,
    ownerName: owner?.name || null,
    admissionTimestamp: admissionDate.toISOString(),
    firstReviewTimestamp,
    stageTransitions: transitions,
    currentStage,
    readinessPercent,
    recommendedProgram: program,
    selectedProgram,
    programScore,
    disqualificationReason: disqualified ? randomPick(DISQUALIFICATION_REASONS) : undefined,
    checklistCompletionPercent,
    blockers,
    changeType: Math.random() < 0.15 ? randomPick([ChangeType.NEW, ChangeType.UPGRADED, ChangeType.CLINICAL_CHANGE]) : undefined,
    transitionedAt: completed ? transitions[transitions.length - 1].exitedAt : undefined,
    locationType: Math.random() < 0.3 ? 'ED' : 'INPATIENT',
  };

  return record;
};

// Cache for generated mock data to ensure consistency across filters
// Version number to invalidate cache when generation logic changes
// Note: Cache now expires daily at 3am MT to refresh "new candidates"
const CACHE_VERSION = 24;
const CACHE_KEY = 'mockAnalyticsData';

/**
 * Generate mock analytics data (cached in localStorage, expires daily at 3am MT)
 */
export const generateMockAnalyticsData = (count: number = 300): PatientAnalyticsRecord[] => {
  // Try to load from localStorage first
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      const parsedCache = JSON.parse(cached);

      // Check if cache is still valid (same version, count, and not expired)
      const currentDayBoundary = getCurrentDayAt3amMT().getTime();
      const cacheExpired = parsedCache.dayBoundary && parsedCache.dayBoundary < currentDayBoundary;

      if (parsedCache.version === CACHE_VERSION &&
          parsedCache.data.length === count &&
          !cacheExpired) {
        return parsedCache.data;
      }
    }
  } catch (error) {
    console.warn('Failed to load cached analytics data:', error);
  }

  // Reset seed for deterministic data generation
  seed = 12345;

  const records: PatientAnalyticsRecord[] = [];

  // Generate records over last 90 days (relative to current date)
  // Using seeded random ensures same patient mix each time
  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - 90);

  for (let i = 0; i < count; i++) {
    records.push(generatePatientRecord(i, { start: startDate, end: endDate }));
  }

  // Cache the generated data with version and day boundary in localStorage
  try {
    const currentDayBoundary = getCurrentDayAt3amMT().getTime();
    localStorage.setItem(CACHE_KEY, JSON.stringify({
      version: CACHE_VERSION,
      data: records,
      dayBoundary: currentDayBoundary,
    }));
  } catch (error) {
    console.warn('Failed to cache analytics data:', error);
  }

  return records;
};

/**
 * Clear the mock data cache (useful for testing or resetting)
 */
export const clearMockDataCache = (): void => {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch (error) {
    console.warn('Failed to clear analytics cache:', error);
  }
};

/**
 * Calculate derived metrics for a patient record
 */
export const calculateDerivedMetrics = (record: PatientAnalyticsRecord): DerivedMetrics => {
  const now = new Date();
  const currentStageTransition = record.stageTransitions.find(t => t.stage === record.currentStage);

  // Time to first review
  const timeToFirstReview = record.firstReviewTimestamp
    ? new Date(record.firstReviewTimestamp).getTime() - new Date(record.admissionTimestamp).getTime()
    : null;

  // Time in current stage
  const timeInStage = currentStageTransition
    ? now.getTime() - new Date(currentStageTransition.enteredAt).getTime()
    : 0;

  // Conversion
  const isConverted = !!record.transitionedAt;

  // Override
  const isOverride = record.selectedProgram !== null && record.selectedProgram !== record.recommendedProgram;

  // Backlog (>48h in non-terminal stage)
  const isBacklog = timeInStage > (48 * 60 * 60 * 1000) &&
                    record.currentStage !== WorkflowStage.DISQUALIFIED &&
                    !isConverted;

  // Aging bucket
  const hoursInStage = timeInStage / (1000 * 60 * 60);
  let agingBucket: '0-24h' | '24-48h' | '2-5d' | '5d+';
  if (hoursInStage < 24) {
    agingBucket = '0-24h';
  } else if (hoursInStage < 48) {
    agingBucket = '24-48h';
  } else if (hoursInStage < 120) {
    agingBucket = '2-5d';
  } else {
    agingBucket = '5d+';
  }

  return {
    timeToFirstReview,
    timeInStage,
    isConverted,
    isOverride,
    isBacklog,
    agingBucket,
  };
};

/**
 * Apply filters to analytics records
 */
export const applyFilters = (
  records: PatientAnalyticsRecord[],
  filters: AnalyticsFilters,
  currentUserId?: string
): PatientAnalyticsRecord[] => {
  return records.filter(record => {
    // Facility filter
    if (filters.facilities.length > 0 && !filters.facilities.includes(record.facilityId)) {
      return false;
    }

    // Location type filter
    if (filters.locationTypes.length > 0 && !filters.locationTypes.includes(record.locationType)) {
      return false;
    }

    // Program filter
    if (filters.programs.length > 0 && !filters.programs.includes(record.recommendedProgram)) {
      return false;
    }

    // Date range filter
    const admissionDate = new Date(record.admissionTimestamp);
    if (admissionDate < filters.dateRange.start || admissionDate > filters.dateRange.end) {
      return false;
    }

    // Owner filter
    if (filters.ownerFilter === 'ME' && record.ownerId !== currentUserId) {
      return false;
    }
    if (filters.ownerFilter === 'UNASSIGNED' && record.ownerId !== null) {
      return false;
    }

    return true;
  });
};

/**
 * Get default date range for a preset
 */
export const getDateRangePreset = (preset: 'today' | '7d' | '30d' | '90d'): { start: Date; end: Date } => {
  const end = new Date();
  const start = new Date();

  switch (preset) {
    case 'today':
      start.setHours(0, 0, 0, 0);
      break;
    case '7d':
      start.setDate(start.getDate() - 7);
      break;
    case '30d':
      start.setDate(start.getDate() - 30);
      break;
    case '90d':
      start.setDate(start.getDate() - 90);
      break;
  }

  return { start, end };
};
